import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastType } from '@elliemae/ds-toast';
import ssf from '@elliemae/em-ssf-guest';
import { PATH_SCENARIO_SEARCH } from '../../../route/constants';
import {
  ChannelOption,
  KNOWN_CHANNEL_LABELS,
  CHANNEL_TO_LOAN_CHANNEL,
  WHOLESALE_CHANNEL_TYPES,
} from '../components/ScenarioHeader/constants';
import { AppDispatch } from '../../../store';
import { getZeroBasedPricingSelector } from '../../../data/lockSummary/actionCreators';

interface UseChannelTypesResult {
  channels: ChannelOption[];
  isLoading: boolean;
  refetch: () => void;
}

export function useChannelTypes(
  selectedChannel: ChannelOption | null,
  setSelectedChannel: (channel: ChannelOption) => void,
  dispatch: AppDispatch,
  setIsSingleChannel?: (value: boolean) => void,
): UseChannelTypesResult {
  const navigate = useNavigate();
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  const fetchChannelTypes = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    try {
      setIsLoading(true);
      const transactionObj = await ssf.getObject('transaction');
      const originData = await transactionObj.getOrigin();

      console.log(
        '[useChannelTypes] SSF channelTypes:',
        originData?.settings?.TPOAdminSettings?.channelTypes,
      );

      const availableChannels: ChannelOption[] = [];

      if (originData?.settings?.TPOAdminSettings?.channelTypes?.length > 0) {
        // Map every SSF channel type to a ChannelOption.
        // Known types (Wholesale, Delegated, NonDelegated)
        // any additional channel types returned by SSF use their raw
        // identifier string as the display label.
        originData?.settings?.TPOAdminSettings?.channelTypes.forEach(
          (channelId) => {
            if (channelId === 'Correspondent') {
              originData?.settings?.TPOAdminSettings?.correspondentSelection.forEach(
                (correspondentChannelId: 'Delegated' | 'NonDelegated') => {
                  availableChannels.push({
                    id: correspondentChannelId,
                    label: KNOWN_CHANNEL_LABELS[correspondentChannelId],
                    wsChannel: WHOLESALE_CHANNEL_TYPES[correspondentChannelId],
                    value: CHANNEL_TO_LOAN_CHANNEL[correspondentChannelId], // Correspondent,
                  });
                },
              );
            } else {
              availableChannels.push({
                id: channelId,
                label: KNOWN_CHANNEL_LABELS[channelId],
                value: CHANNEL_TO_LOAN_CHANNEL[channelId],
              });
            }
          },
        );
      } else {
        toast(
          {
            type: ToastType.ERROR,
            messageTitle: 'No Channels Available',
            messageText: 'No supported channel types were returned from TPOC',
          },
          {},
        );
      }

      // If only one channel is available, skip the Channel Select page
      // entirely and navigate directly to Search.
      if (availableChannels.length === 1) {
        setIsSingleChannel?.(true);
        setSelectedChannel(availableChannels[0]);
        await dispatch(getZeroBasedPricingSelector(availableChannels[0].id) as any);
        navigate(PATH_SCENARIO_SEARCH, { replace: true });
        return;
      }

      setChannels(availableChannels);

      // Only set the default selection if no channel is already selected.
      // This holds the user's choice when navigating back from Search.
      if (!selectedChannel) {
        setSelectedChannel(availableChannels[0]);
      }
    } catch (err) {
      console.error('Error fetching channel types:', err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedChannel, setSelectedChannel, dispatch]);

  useEffect(() => {
    fetchChannelTypes();
  }, [fetchChannelTypes]);

  return {
    channels,
    isLoading,
    refetch: fetchChannelTypes,
  };
}
